import Color from 'color';
import formatDateIntl from 'date-fns/intlFormat/index';

import {
    COLOR_DARK,
    COLOR_LIGHT,
    DISABLED_IN_PREVIEW,
    PLACEHOLDERS_KEY,
    PREVIEW_MODE,
    TRACKING_KEY,
} from './global/constants';
import getQueryParameter from './global/helper/get-query-parameter';

import type { Theme, ThemeV2 } from '$lib/types/Theme';
import type { TrackingProperty } from '$lib/types/TrackingProperty';

declare global {
    interface String {
        splice(start: number, length: number, replacement: string): string;
    }
}

// For personalization string replacement
String.prototype.splice = function (start: number, length: number, replacement: string) {
    return this.substr(0, start) + replacement + this.substr(start + length);
};

export const hexToRgb = (hex: string) => {
    // hex can be #123 or #112233
    const sanitizedHexString =
        hex.length === 4 ? '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3] : hex;

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(sanitizedHexString);

    if (!result) return { r: 0, g: 0, b: 0 };

    return {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
    };
};

export const rgbaToRgb = (rgba: string) => {
    const colors = rgba.match(/\d+/g);

    if (!colors) return { r: 0, g: 0, b: 0 };

    return {
        r: parseInt(colors[0], 16),
        g: parseInt(colors[1], 16),
        b: parseInt(colors[2], 16),
    };
};

export const isHexString = (color: string) => {
    return /^#([A-Fa-f0-9]{3}){1,2}$/i.test(color);
};

export const isRgbaString = (color: string) => {
    return /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/.test(color);
};

export const isDark = (color: string) => {
    if (color === 'transparent') {
        return false;
    }

    try {
        const [r, g, b] = Color(color).rgb().array();
        const yiq = (r * 2126 + g * 7152 + b * 722) / 10000;
        return yiq < 128;
    } catch {
        return false;
    }
};

export const getColorByLightness = (refColor = '#000000') => {
    if (refColor.includes('linear-gradient')) return COLOR_LIGHT;

    if (refColor === 'transparent') return COLOR_DARK;

    return Color(refColor).lightness() <= 60 ? COLOR_LIGHT : COLOR_DARK;
};

/**
 * Get cookie preferences from local storage.
 *
 * Always resolves to a plain object. The entry is JSON parsed from localStorage, so a
 * corrupted, truncated or hand-edited value can throw or parse to `null` / a string / an
 * array — and localStorage access itself throws in an embedded funnel where third-party
 * storage is blocked. Callers run during layout init, where an uncaught throw stops the
 * whole funnel from rendering, so every failure degrades to "no decision recorded" and the
 * consent banner asks again.
 */
export const getCookiePreferences = (campaignId: string, campaignVersionId: string) => {
    try {
        const preferences = window.localStorage.getItem(
            `${TRACKING_KEY}${campaignId}.${campaignVersionId}`,
        );

        if (!preferences) {
            return {};
        }

        const parsed = JSON.parse(preferences);

        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
            return {};
        }

        // Keep only genuine per-integration decisions. setCookiePreferences only ever writes
        // booleans, so any other value means the entry was tampered with or written by a
        // different schema — it must neither grant consent nor suppress the banner.
        //
        // Integration *names* are deliberately not whitelisted. The display names originate
        // from the integration schemas in lemon-tool-api and are mirrored by hand in
        // global/tracking/integrations.ts, so filtering against that copy would silently drop
        // a visitor's decision for any integration added to the API but not yet mirrored here.
        const decisions: Record<string, boolean> = {};

        Object.keys(parsed).forEach((integration) => {
            if (typeof parsed[integration] === 'boolean') {
                decisions[integration] = parsed[integration];
            }
        });

        return decisions;
    } catch {
        return {};
    }
};

/**
 * Whether a usable consent decision is stored.
 *
 * Deliberately derived from the parsed preferences rather than the raw entry: a corrupted
 * value is present but not a decision, and treating it as one would hide the banner forever
 * without the visitor ever having been asked.
 */
export const getCookiePreferencesPresent = (campaignId: string, campaignVersionId: string) => {
    return Object.keys(getCookiePreferences(campaignId, campaignVersionId)).length > 0;
};

/**
 * Set cookie preferences in local storage
 */
export const setCookiePreferences = (
    campaignId: string,
    campaignVersionId: string,
    preferences: { [key: string]: any },
) => {
    const LS = window.localStorage;

    return LS.setItem(
        `${TRACKING_KEY}${campaignId}.${campaignVersionId}`,
        JSON.stringify(preferences),
    );
};

/**
 * Add Http to URLs without http://, https://, tel: or mailto: prefix
 */
export const createExternalUrl = (
    url: string,
    campaignId?: string,
    trackingProperties?: TrackingProperty[],
) => {
    const { utm, custom, thirdParty } = getQueryParameter();
    let savedTrackingProperties = {};
    let defaultTrackingProperties = {};
    let allTrackedData = {};

    // makes sure all tracked data is used instead of just the ones set in the tracking properties
    // the `getQueryParameter` only gets the query parameters that are set in the current URL
    // we need to get all query parameters which were tracked on funnel load
    if (typeof window !== 'undefined') {
        allTrackedData = window.analytics?.user?.()?.traits();
    }

    if (campaignId) {
        savedTrackingProperties = getAllTrackingProperties(campaignId);
    }

    if (trackingProperties) {
        defaultTrackingProperties = getDefaultTrackingProperties(trackingProperties);
    }

    const processedUrl = replaceCustomVariables(url, {
        ...defaultTrackingProperties,
        ...savedTrackingProperties,
        ...thirdParty,
        ...utm,
        ...custom,
        ...allTrackedData,
    })
        // FUN-4086: Surrounding whitespace is trimmed because customers routinely paste link
        // targets with a stray leading space (` tel:+39…`). Left in place it hides the protocol
        // from the check below, so `http://` gets prefixed and the resulting
        // `http:// tel:+39…` throws when handed to `location.assign` — the click does nothing.
        .trim();

    // Checked against the substituted URL, not the template: a URL that starts with a merge tag
    // (e.g. `@redirect_url`) only reveals its protocol once the variable has been replaced.
    if (!/(^mailto:)|(^tel:)|(^sms:)|(^https?:\/\/)/i.test(processedUrl)) {
        return 'http://' + processedUrl;
    }

    return processedUrl;
};

type CustomVariables = { [key: string]: string | (string | null)[] | null };

/**
 * Merge tags are written as `@<variable>` in customer-configured URLs. The token charset is
 * deliberately generous because tracking property field names and third party query parameter
 * names may contain dots and dashes — `resolveMergeTag` narrows a token down to the longest
 * variable we actually know about.
 */
const MERGE_TAG_PATTERN = /@([a-zA-Z0-9_.-]+)/g;

/**
 * Resolves the longest known variable at the start of a merge tag token.
 *
 * `@name_last` must not be shadowed by a shorter `name` variable, while `@foo.bar` should still
 * resolve `foo` when that is the only known variable. So we shrink the token from the right
 * until we hit a variable we know, and hand the remainder back to the caller.
 *
 * Resolving on existence rather than on having a non-empty value matters twice over: a known
 * variable that is merely unset now renders as an empty parameter instead of leaking the raw
 * `@variable` text into the customer's outbound URL (matching how embed blocks interpolate),
 * and a shorter variable can no longer win over a longer exact match that happens to be
 * empty, which produced mangled output like `@name` becoming `Xe`.
 */
const resolveMergeTag = (token: string, customVariables: CustomVariables) => {
    for (let length = token.length; length > 0; length -= 1) {
        const key = token.slice(0, length);

        // `hasOwnProperty` guard: without it, tokens like `@toString` or `@constructor` would
        // resolve against Object.prototype and inject native code into the URL.
        if (!Object.prototype.hasOwnProperty.call(customVariables, key)) {
            continue;
        }

        return { value: customVariables[key], rest: token.slice(length) };
    }

    return null;
};

const substituteMergeTags = (
    segment: string,
    customVariables: CustomVariables,
    encodeValues: boolean,
) => {
    // A replacer function is used so that `$&`, `$1` and friends inside an answer are inserted
    // verbatim instead of being interpreted as replacement patterns.
    return segment.replace(MERGE_TAG_PATTERN, (match, token: string) => {
        const resolved = resolveMergeTag(token, customVariables);

        if (!resolved) {
            return match;
        }

        // Tracking properties are commonly declared without a `defaultValue`, so a known but
        // unset variable arrives as null/undefined and must render empty rather than as the
        // string "undefined".
        const value = PREVIEW_MODE ? DISABLED_IN_PREVIEW : String(resolved.value ?? '');

        return (encodeValues ? encodeURIComponent(value) : value) + resolved.rest;
    });
};

/**
 * Index at which the query string (or fragment) starts, or -1 when the URL has neither.
 */
const findQueryStart = (url: string) => {
    const queryIndex = url.indexOf('?');
    const fragmentIndex = url.indexOf('#');

    if (queryIndex === -1) return fragmentIndex;

    if (fragmentIndex === -1) return queryIndex;

    return Math.min(queryIndex, fragmentIndex);
};

/**
 * Replaces `@variable` merge tags in a customer-configured URL with tracked values.
 *
 * FUN-4081: Values that land in the query string (or fragment) are percent-encoded. Without
 * that, characters which are structural in `application/x-www-form-urlencoded` get silently
 * reinterpreted by the receiving server — most visibly a literal `+` (as in `€500K+`) arriving
 * as a space, but `&` and `=` can also inject or truncate query parameters.
 *
 * Tags in the origin/path portion are left raw on purpose: those are used to assemble the URL
 * itself (dynamic subdomains, or a full URL passed through as `@redirect_url`), and encoding
 * them would turn `https://…` into `https%3A%2F%2F…` and break the link.
 */
const replaceCustomVariables = (url: string, customVariables: CustomVariables) => {
    const queryStart = findQueryStart(url);

    if (queryStart === -1) {
        return substituteMergeTags(url, customVariables, false);
    }

    return (
        substituteMergeTags(url.slice(0, queryStart), customVariables, false) +
        substituteMergeTags(url.slice(queryStart), customVariables, true)
    );
};

export const setTrackingProperty = (
    key: string,
    value: string | undefined,
    campaignId: string | undefined,
) => {
    const ST = window.sessionStorage;
    const personalization = JSON.parse(ST.getItem(`${PLACEHOLDERS_KEY}${campaignId}`) || '{}');

    const updatedPersonalization = personalization ? { ...personalization } : {};

    updatedPersonalization[key] = value;

    ST.setItem(`${PLACEHOLDERS_KEY}${campaignId}`, JSON.stringify(updatedPersonalization));
};

export const getAllTrackingProperties = (campaignId: string) => {
    const ST = window.sessionStorage;
    const properties = ST.getItem(`${PLACEHOLDERS_KEY}${campaignId}`);

    if (!properties) return {};

    return JSON.parse(ST.getItem(`${PLACEHOLDERS_KEY}${campaignId}`) || '{}');
};

export const getDefaultTrackingProperties = (trackingProperties: TrackingProperty[]) => {
    const mappedTrackingProperties: { [fieldName: string]: string } = {};

    trackingProperties.forEach((property) => {
        mappedTrackingProperties[property.fieldName] = property.defaultValue;
    });

    return mappedTrackingProperties;
};

export const parseISODate = (str: string, locale: string) => {
    if (isIsoDate(str)) {
        const date = new Date(str);

        if (!isNaN(date.valueOf())) {
            return formatDateIntl(
                date,
                { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
                { locale },
            );
        }
    }

    return str;
};

const isIsoDate = (str: string) => {
    if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(str)) return false;

    const d = new Date(str);

    return d.toISOString() === str; // valid date
};

export const getColor = (color: string, themeColor: string) => {
    return !color || color === 'transparent' ? themeColor : color;
};

export const insertIntoArray = (arr: any[], value: any) => {
    return arr.reduce((result, element, index, array) => {
        result.push(element);

        if (index < array.length - 1) {
            result.push(value);
        }

        return result;
    }, []);
};

export const getFontName = (theme: Theme, fallbackFontName = 'Roboto') => {
    const { typography, fontId } = theme;

    if (fontId) {
        return fontId;
    }

    return typography.includes(':') ? typography.split(':')[0] : fallbackFontName;
};

export const getHeadingFontName = (theme: ThemeV2): string | undefined => {
    const { headingFontId, headingFontFamily } = theme;

    if (headingFontId) {
        return headingFontId;
    }

    if (headingFontFamily) {
        return headingFontFamily.includes(':')
            ? headingFontFamily.split(':')[0]
            : headingFontFamily;
    }

    return undefined;
};
